import React, { useContext } from 'react';
import { FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Button } from '../shared/simple/Button';
import { Label } from '../shared/simple/Label';
import { ModalQuestion } from '../shared/simple/ModalQuestion';
import { OptInModal } from '../user/HomepageStarterEmail/OptInModal/OptInModal';
import { SubTitle } from '../shared/simple/SubTitle';
import { Category } from './Category';
import { LinkText } from './styled';
import { ModuleContext } from '../../context/ModuleContext';
import { Row, Col } from 'react-bootstrap';
import { StyledRow, StepsContainer, StyledLink, StepItem } from '../user/styled';
import { StyledFormTier } from '../shared/simple/StyledFormTier';
import { Token } from './Token';
import { trimLowerCase } from '../../helpers/shared';
import { contactSMS, paramsAdd, paramsEdit, slimTier, advancedTier, deeperTier, speedTier, hiddenField } from '../../constants/form';
import { baseField } from '../../constants/base';
import { Loading } from '../shared/Loading';
import { useSelector } from 'react-redux';
import { useFormTier } from '../../hooks/useFormTier';
import { useDescription } from '../../hooks/useProducts'
import { useTextLanguage } from '../../hooks/useShared'
import { useFormSend, useInitFormTier } from '../../hooks/useFormTier';
import { useIbvShown } from '../../hooks/useIbvDialog';
import { useOptInModal } from '../../hooks/useOptInModal';
import { useGetProductRequest, useTiers, useCompletedForm } from '../../hooks/useTier';
import { useParams } from 'react-router-dom';
import { useInitLivenessDetection } from '../../hooks/useLivenessDetection';
import { useSwitchDesign } from '../../hooks/useSwitchDesign'
import Stepper from './Stepper/Stepper';
import ErrorMessages from './Stepper/ErrorMessages';
import { useDetectAndFixVisibleHiddenInputs } from '../../hooks/useShared';

/**
 * Component form for tiers
 * @param {Function} handleOpenIbv
 * @param {String}   className      -  optional string for customzing the Form's classname
 * @param {Boolean}  isNewDesign    -  boolean to define custom appearance
 * @returns
 * @component
 */
export const Form = ({
  handleOpenIbv,
  className
}) => {
  useTiers()
  const { t } = useTranslation();
  const [categoriesTier, tier, tierFieldState] = useContext(ModuleContext);
  const { data } = useSelector(state => state.personData);
  const { description } = useTextLanguage();
  const { action, loading, schemaTier } = useInitFormTier(tierFieldState);
  const { hideCategories, methods, onRouteProfile, steps } = useFormTier(
    tier, tierFieldState, categoriesTier, schemaTier);
  const {
    activeGroup,
    highstStep,
    lastTier,
    modalIsOpen,
    modalMessage,
    onBackStep,
    onChangeStep,
    onContinueStep,
    onFinishRequest,
    onSave,
    onSubmit,
    openModal,
    setOpenModal,
    sendTier,
    setModalIsOpen,
    setFinishChanges,
    finishChanges,
    setActiveGroupIndex,
    activeGroupIndex,
    arrayToValidate,
    arrayToValidateInStep
  } = useFormSend(hideCategories, methods, tier, tierFieldState, steps, handleOpenIbv);
  const { isOptInModalOpen, setIsOptInModalOpen } = useOptInModal(setFinishChanges)
  const {
    flagRequest,
    actualRequestSuccessOrRejected
  } = useDescription();
  useCompletedForm(methods);
  const { isIBVVerified } =useIbvShown(activeGroup)
  useGetProductRequest()
  const params = useParams()
  const { isNewDesign } = useSwitchDesign()

  const [hasEffectRun, setHasEffectRun] = React.useState(false);

  React.useEffect(() => {
    if (isIBVVerified && !hasEffectRun) {
      setFinishChanges(true); // Update finishChanges
      setHasEffectRun(true);  // Prevent further updates
    }
  }, [isIBVVerified, hasEffectRun]);
  
  /* Submitting form automatically, on a multi-step tier, if 'finishChanges' */
  React.useEffect(() => {
    const isSlimAdvanced = params?.codeTier && (params.codeTier === slimTier)
    const isAdvanced = params?.codeTier && (params.codeTier === advancedTier)
    const isDeeper = params?.codeTier && (params.codeTier === deeperTier)
    const isSpeedForm = params?.codeTier && (params.codeTier === speedTier)
    if (finishChanges && (action === paramsAdd) && (isSlimAdvanced || isAdvanced || isDeeper || isSpeedForm)) {
      setTimeout(() => {
        onChangeStep(); 
      }, 100);
    }
  }, [finishChanges]);

  useInitLivenessDetection()
  useDetectAndFixVisibleHiddenInputs(methods)

  return (
    <StyledRow 
      className="p-0"
      isNewDesign={isNewDesign}  
    >
      <FormProvider { ...methods }>
        <StyledFormTier
          className={`
            col-xs-12 
            ${className?.includes('p-') ? className.slice(className?.indexOf('p-'), className?.indexOf('p-')+3) : 'p-3'}  
            ${className?.includes('pt-') ? className.slice(className?.indexOf('pt-'), className?.indexOf('pt-')+4) : 'pt-0'} `
          }
          onSubmit={ methods.handleSubmit(onSubmit) }
          novalidate
        >
          {/* 'Only Back button' case */}
          {(activeGroup > steps[0]) && (
            <div style={{ display:'flex', justifyContent:'end', position: 'absolute', top: '20px', right: '40px' }}>
              <LinkText
                color="third"
                onClick={ onBackStep }
              >
                {`< ${ t("common.back") }`}
              </LinkText>
            </div>)}

          {/* Old and new steppers inside Stepper component */}
          <Stepper 
            steps={steps}
            activeGroup={activeGroup}
            onClick={setActiveGroupIndex}
            version='2'
          />

          <ErrorMessages 
            errorsList={arrayToValidate}
            description={description}
            onClick={setActiveGroupIndex}
            activeGroupIndex={activeGroupIndex}
          />

          {hideCategories?.map(category => {
            return (
              (category.unique_name === baseField.modalValidateSms) && (
                <Token
                  category={ category }
                  key={`token_${category.unique_name}_${category.id}`}
                  openModal={ openModal }
                  setOpenModal={setOpenModal}
                  tier={ tier }
                  tierFieldState={ tierFieldState }
                  isNewDesign={isNewDesign}
                  setFinishChanges={setFinishChanges}
                />
              )
            );
          })}
          {hideCategories?.filter(item => trimLowerCase(
            item.unique_name) !== baseField.modalValidateSms).map((
              category, index, row) => {
            return (
              <div
                className={`col-12 row ${ isNewDesign ? 'text-light' : 'text-dark' } ${category.action}`}
                style={ (category.step !== activeGroup) ? { display: "none" } : {} }
                id={`module_${category.id}`}
                key={`fragment_${category.unique_name}_${category.id}`}
              >
                { category?.active && (
                  <SubTitle
                    className="mb-2 mt-3"
                    key={`subtitle_${category.unique_name}_${category.id}`}
                  >
                    { category[description] }
                  </SubTitle>
                )}
                {(category.unique_name === contactSMS && action === paramsEdit) && (
                  <Label isNewDesign={isNewDesign}>
                    { t("profile.sms") }&nbsp;
                    <StyledLink
                      className="p-0"
                      onClick={ onRouteProfile }
                      color="first"
                      xs={12}
                    >
                      { t("profile.name") }
                    </StyledLink>
                  </Label>
                )}
                <Category
                  category={ category }
                  key={`category_${category.unique_name}_${category.id}`}
                  isNewDesign={ isNewDesign }
                />
              </div>
            );
          })}
          <Row className="justify-content-center m-0">
            <Col className="p-3 mt-5 mb-5" xs={12} md={6} xl={4}>
              {(loading && !isNewDesign)
                ? (
                  <Loading />
                ) : ((highstStep === activeGroup && sendTier) ? (
                  <>
                    {(action === paramsAdd) && (
                      <Button
                        className="btn mb-0"
                        type="button"
                        color="first"
                        onClick={ onSave }
                        name="btn_save"
                      >
                        { t("common.submit") }
                      </Button>
                    )}
                    {((!flagRequest && !actualRequestSuccessOrRejected) && action === paramsEdit && tier.verification_level !== lastTier.verification_level) && (
                      <Button
                        className="btn mb-0"
                        type="button"
                        color="first"
                        onClick={ onSave }
                        name="btn_next"
                      >
                        { t("common.nextTier") }
                      </Button>
                    )}
                    {(!flagRequest && !actualRequestSuccessOrRejected) && (action === paramsEdit) && data.last_migrated_tier && (lastTier.checked || lastTier.unique_name === speedTier) && (
                      <Button
                        className="btn mb-0"
                        type="button"
                        color={ isNewDesign ? 'first' : 'fourth'}
                        onClick={ onFinishRequest }
                        name="btn_finish"
                      >
                        { t("common.finishRequest") }
                      </Button>
                    )}
                  </>
                ) : (
                  <Button
                    className="btn mb-0"
                    type="button"
                    color={ isNewDesign ? 'first' : 'fourth'}
                    onClick={ onContinueStep }
                    name="btn_continue"
                  >
                    { t("common.continue") }
                  </Button>
                ))
              }
            </Col>
            <ModalQuestion
              modalIsOpen={ modalIsOpen }
              setModal={ setModalIsOpen }
              onChangeStep={ onChangeStep }
              message={ modalMessage }
            />
            <OptInModal 
              handleOpenIbv={handleOpenIbv}
              modalIsOpen={ isOptInModalOpen }
              setModal={ setIsOptInModalOpen }
              onChangeStep={ onChangeStep }
            />
          </Row>
        </StyledFormTier>
      </FormProvider>
    </StyledRow>
  );
};